import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import useHlsMedia from "../../hooks/useHlsMedia";
import { savePosition } from "../../ReusableFunctions/playbackPosition";
import { getPositionSeconds } from "../api/positionSeconds";
import { pushPosition, flushPositions } from "../api/progressSync";
import { trackEvent } from "../../analytics";
import {
  trackLoaded,
  playingChanged,
  positionChanged,
  durationChanged,
  speedCycled,
  mutedChanged,
  volumeChanged,
  queueChanged,
  expandedChanged,
  cardOnScreenChanged,
} from "../../store/slices/PlayerSlice";

/*
 * Owns the TWO persistent media elements (one <audio>, one <video>) that
 * survive navigation — mounted in RootShell, outside the route outlet.
 * The video element is a single DOM node that gets REPARENTED between the
 * class-page player card, the mini-player, and the expanded bottom sheet via
 * attachVideo()/detachVideo() — element reuse, not re-instantiation, so
 * playback never restarts when the user navigates or expands the sheet.
 *
 * Resume: saved localStorage position (playbackPosition.js) unless the caller
 * passes startAt (deep links, mode switches). Positions save on pause, on
 * unload, and every 15s while playing — the cadence Phase 6 reuses for
 * account sync.
 */

const PlayerContext = createContext(null);

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used inside PlayerProvider");
  return ctx;
}

const POSITION_SAVE_INTERVAL_MS = 15000;

export default function PlayerProvider({ children }) {
  const dispatch = useDispatch();
  const audioRef = useRef(null);
  const videoRef = useRef(null); // stable {current} for hooks + imperative reparenting
  const videoHolderRef = useRef(null); // offscreen parking spot for the video element
  const currentRef = useRef(null);
  const startAtRef = useRef(0);
  const pendingAutoplayRef = useRef(null); // media URL to play once its stream attaches
  const videoClaimsRef = useRef([]); // active {container, priority} claims on the video node
  const completeFiredRef = useRef(null); // media URL whose audio_complete already fired

  const current = useSelector((s) => s.player.current);
  const speed = useSelector((s) => s.player.speed);
  const muted = useSelector((s) => s.player.muted);
  const volume = useSelector((s) => s.player.volume);
  const queue = useSelector((s) => s.player.queue);
  const queueRef = useRef(queue);
  queueRef.current = queue;
  currentRef.current = current;

  const isVideo = current?.mediaKind === "video";
  const activeEl = () => (currentRef.current?.mediaKind === "video" ? videoRef.current : audioRef.current);

  // Move the video node into the highest-priority active claim's container
  // (ties go to the most recent claim), or park it offscreen when unclaimed.
  const placeVideo = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const claims = videoClaimsRef.current;
    const target = claims.length
      ? claims.reduce((best, c) => (c.priority >= best.priority ? c : best)).container
      : videoHolderRef.current;
    if (target && v.parentElement !== target) target.appendChild(v);
  }, []);

  // Stream sources attach in useHlsMedia's effect, AFTER the render that set
  // them (hls.js loads async on top of that) — so load() can't play() a
  // stream item directly; it parks the intent in pendingAutoplayRef and this
  // callback fires it once the element actually has a source. Stable
  // identity (deps: [dispatch]) or the hook would re-create hls each render.
  const onStreamReady = useCallback(
    (el) => {
      const item = currentRef.current;
      const url = item?.src || item?.streamUrl;
      if (url && pendingAutoplayRef.current === url) {
        pendingAutoplayRef.current = null;
        el.play().catch(() => dispatch(playingChanged(false)));
      }
    },
    [dispatch]
  );

  // HLS attach for whichever element the current item plays through.
  useHlsMedia(audioRef, !isVideo && current?.streamUrl ? current.streamUrl : null, startAtRef.current, onStreamReady);
  useHlsMedia(videoRef, isVideo && current?.streamUrl ? current.streamUrl : null, startAtRef.current, onStreamReady);

  // Lazily create the persistent <video> node and wire its events once.
  useEffect(() => {
    if (videoRef.current) return;
    const v = document.createElement("video");
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.preload = "none";
    v.controls = false;
    v.style.width = "100%";
    v.style.height = "100%";
    v.style.objectFit = "contain";
    v.style.background = "#0D1420";
    videoRef.current = v;
    placeVideo(); // honor any claims registered before the node existed
    const guard = (fn) => () => {
      if (currentRef.current?.mediaKind === "video") fn(v);
    };
    v.addEventListener("timeupdate", guard((el) => dispatch(positionChanged(el.currentTime))));
    const onDur = guard((el) => Number.isFinite(el.duration) && dispatch(durationChanged(el.duration)));
    v.addEventListener("loadedmetadata", onDur);
    v.addEventListener("durationchange", onDur);
    v.addEventListener("play", guard(() => dispatch(playingChanged(true))));
    v.addEventListener(
      "pause",
      guard((el) => {
        dispatch(playingChanged(false));
        const item = currentRef.current;
        if (item && el.currentTime > 0) {
          savePosition(item.src || item.streamUrl, el.currentTime);
          pushPosition({ classId: item.classId, positionSeconds: el.currentTime, mediaKind: item.mediaKind });
        }
      })
    );
    v.addEventListener(
      "ended",
      guard(() => {
        dispatch(playingChanged(false));
        apiRef.current?.playNextInQueue();
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply speed / volume / mute to both elements whenever they change.
  useEffect(() => {
    [audioRef.current, videoRef.current].forEach((el) => {
      if (!el) return;
      el.playbackRate = speed;
      el.muted = muted;
      el.volume = volume;
    });
  }, [speed, muted, volume]);

  // Periodic + unload position saves (active element only). localStorage is
  // the fast path; signed-in users also sync to the account (debounced).
  useEffect(() => {
    const save = () => {
      const el = activeEl();
      const item = currentRef.current;
      if (el && item && !el.paused && el.currentTime > 0) {
        savePosition(item.src || item.streamUrl, el.currentTime);
        pushPosition({ classId: item.classId, positionSeconds: el.currentTime, mediaKind: item.mediaKind });
        // Brief §9: audio_complete at the 75% milestone, once per track.
        const url = item.src || item.streamUrl;
        if (el.duration > 0 && el.currentTime / el.duration >= 0.75 && completeFiredRef.current !== url) {
          completeFiredRef.current = url;
          try {
            trackEvent({ category: "player", action: "audio_complete", label: item.title });
          } catch (_) {}
        }
      }
    };
    const interval = setInterval(save, POSITION_SAVE_INTERVAL_MS);
    const onUnload = () => {
      const el = activeEl();
      const item = currentRef.current;
      if (el && item && el.currentTime > 0) {
        savePosition(item.src || item.streamUrl, el.currentTime);
        pushPosition({ classId: item.classId, positionSeconds: el.currentTime, mediaKind: item.mediaKind });
        flushPositions();
      }
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") onUnload();
    };
    window.addEventListener("beforeunload", onUnload);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", onUnload);
      document.removeEventListener("visibilitychange", onHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apiRef = useRef(null);
  const api = useMemo(() => {
    const applyMediaSession = (item) => {
      if (!("mediaSession" in navigator)) return;
      try {
        // eslint-disable-next-line no-undef
        navigator.mediaSession.metadata = new MediaMetadata({
          title: item.title || "Inside Chassidus",
          artist: "Rabbi Yossi Paltiel",
          album: item.seriesTitle || "Inside Chassidus",
        });
        navigator.mediaSession.setActionHandler("play", () => api.play());
        navigator.mediaSession.setActionHandler("pause", () => api.pause());
        navigator.mediaSession.setActionHandler("seekbackward", () => api.seekBy(-10));
        navigator.mediaSession.setActionHandler("seekforward", () => api.seekBy(10));
      } catch (_) {
        /* MediaSession unsupported */
      }
    };

    const obj = {
      audioRef,
      videoRef,

      /* Claim the persistent video element for a surface. The node lives in
       * whichever active claim has the highest priority — expanded sheet (3)
       * > class-page card (2) > mini-player thumb (1) — and parks in the
       * offscreen holder when nothing claims it. Mount/unmount ordering
       * between surfaces stops mattering: releasing a claim re-places the
       * node into the next-best claimant instead of unconditionally parking
       * it. Returns a release function (use directly as effect cleanup). */
      claimVideo(container, priority = 1) {
        if (!container) return () => {};
        const claim = { container, priority };
        videoClaimsRef.current = [...videoClaimsRef.current, claim];
        placeVideo();
        return () => {
          videoClaimsRef.current = videoClaimsRef.current.filter((c) => c !== claim);
          placeVideo();
        };
      },

      /* Load a track. item: see PlayerSlice. startAt overrides saved resume. */
      load(item, { autoplay = true, startAt } = {}) {
        if (!item) return;
        const el = item.mediaKind === "video" ? videoRef.current : audioRef.current;
        const other = item.mediaKind === "video" ? audioRef.current : videoRef.current;
        if (!el) return;
        if (other) other.pause();

        const mediaUrl = item.src || item.streamUrl;
        const resumeAt = typeof startAt === "number" ? startAt : getPositionSeconds(mediaUrl);
        startAtRef.current = resumeAt;
        // Read BEFORE trackLoaded: is this stream already attached from a
        // previous load of the same item? (currentRef still holds the old
        // item — it updates on the next render.)
        const streamAlreadyAttached =
          !!item.streamUrl && (currentRef.current?.src || currentRef.current?.streamUrl) === mediaUrl;
        dispatch(trackLoaded({ item, autoplay, startAt: resumeAt }));

        if (!item.streamUrl && item.src) {
          el.src = item.src;
          if (resumeAt > 0) {
            const seek = () => {
              try {
                el.currentTime = resumeAt;
              } catch (_) {}
            };
            el.addEventListener("loadedmetadata", seek, { once: true });
          }
        }
        applyMediaSession(item);
        try {
          trackEvent({ category: "player", action: item.mediaKind === "video" ? "video_play" : "audio_play", label: item.title });
        } catch (_) {}
        if (item.streamUrl && !streamAlreadyAttached) {
          // Stream source isn't on the element yet (useHlsMedia attaches it
          // after this render) — a play() here would reject on a source-less
          // element and the first click would appear to do nothing. Park the
          // intent; onStreamReady fires it the moment the source attaches.
          pendingAutoplayRef.current = autoplay ? mediaUrl : null;
        } else if (autoplay) {
          el.play().catch(() => dispatch(playingChanged(false)));
        }
      },

      play() {
        activeEl()?.play().catch(() => {});
      },
      pause() {
        activeEl()?.pause();
      },
      toggle() {
        const el = activeEl();
        if (!el) return;
        if (el.paused) el.play().catch(() => {});
        else el.pause();
      },
      seekTo(seconds) {
        const el = activeEl();
        if (!el) return;
        try {
          el.currentTime = Math.max(0, seconds);
          dispatch(positionChanged(el.currentTime));
        } catch (_) {}
      },
      seekBy(delta) {
        const el = activeEl();
        if (!el) return;
        obj.seekTo(el.currentTime + delta);
      },
      currentTime() {
        return activeEl()?.currentTime || 0;
      },
      isPaused() {
        const el = activeEl();
        return el ? el.paused : true;
      },
      cycleSpeed() {
        dispatch(speedCycled());
      },
      setMuted(m) {
        dispatch(mutedChanged(m));
      },
      setVolume(v) {
        dispatch(volumeChanged(v));
      },
      setQueue(items) {
        dispatch(queueChanged(items));
      },
      setExpanded(v) {
        dispatch(expandedChanged(v));
      },
      setCardOnScreen(v) {
        dispatch(cardOnScreenChanged(v));
      },
      playNextInQueue() {
        const q = queueRef.current;
        if (!q || q.length === 0) return;
        const [next, ...rest] = q;
        // Items without a media source (series up-next resolved on its own
        // page) are navigation targets, not playable — handled by the sheet.
        if (!next.src && !next.streamUrl) return;
        dispatch(queueChanged(rest));
        obj.load(next, { autoplay: true });
      },
    };
    apiRef.current = obj;
    return obj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Audio element event wiring (guarded to audio-mode items).
  const audioGuard = (fn) => () => {
    if (currentRef.current?.mediaKind !== "video") fn(audioRef.current);
  };
  const onTimeUpdate = audioGuard((el) => el && dispatch(positionChanged(el.currentTime)));
  const onDuration = audioGuard((el) => el && Number.isFinite(el.duration) && dispatch(durationChanged(el.duration)));
  const onPlay = audioGuard(() => dispatch(playingChanged(true)));
  const onPause = audioGuard((el) => {
    dispatch(playingChanged(false));
    const item = currentRef.current;
    if (el && item && el.currentTime > 0) {
      savePosition(item.src || item.streamUrl, el.currentTime);
      pushPosition({ classId: item.classId, positionSeconds: el.currentTime, mediaKind: item.mediaKind });
    }
  });
  const onEnded = audioGuard(() => {
    dispatch(playingChanged(false));
    api.playNextInQueue();
  });

  return (
    <PlayerContext.Provider value={api}>
      {children}
      {/* Persistent media elements — never unmount across navigation */}
      <audio
        ref={audioRef}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onDuration}
        onDurationChange={onDuration}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        preload="none"
        style={{ display: "none" }}
      />
      {/* Offscreen parking for the shared <video> node when no surface shows it */}
      <div ref={videoHolderRef} style={{ position: "fixed", width: 0, height: 0, overflow: "hidden" }} aria-hidden="true" />
    </PlayerContext.Provider>
  );
}
